import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  faUser, 
  faBuilding, 
  faClipboardList, 
  faCog, 
  faEdit, 
  faEye, 
  faCalendarAlt, 
  faMapMarkerAlt, 
  faBed, 
  faBath, 
  faRulerCombined,
  faCheckCircle,
  faTimesCircle,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import Translation from '../components/common/Translation';
// Import CSS without relying on exact filename case
import '../styles/header.css'; // We know this file exists and loads correctly

const UserProfilePage = () => {
  const [activeTab, setActiveTab] = useState('account');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [userData, setUserData] = useState(null);
  const [viewedProperties, setViewedProperties] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  
  const { user, token, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Handle tab selection from URL query parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    
    if (tabParam && ['account', 'properties', 'requests', 'settings'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);
  
  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);
  
  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const response = await fetch(`/api/user/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setUserData(data.data);
          setFormData({
            name: data.data.name || '',
            email: data.data.email || '',
            phone: data.data.phone || '',
            address: data.data.address || ''
          });
        } else {
          throw new Error(data.message || 'Failed to fetch user data');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [token]);
  
  // Fetch viewed properties
  useEffect(() => {
    const fetchViewedProperties = async () => {
      if (!token || activeTab !== 'properties') return;
      
      setLoading(true);
      try {
        const response = await fetch(`/api/user/viewed-properties`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setViewedProperties(data.data);
        } else {
          throw new Error(data.message || 'Failed to fetch viewed properties');
        }
      } catch (err) {
        console.error('Error fetching viewed properties:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchViewedProperties();
  }, [token, activeTab]);
  
  // Fetch maintenance requests
  useEffect(() => {
    const fetchMaintenanceRequests = async () => {
      if (!token || activeTab !== 'requests') return;
      
      setLoading(true);
      try {
        const response = await fetch(`/api/user/maintenance-requests`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setMaintenanceRequests(data.data);
        } else {
          throw new Error(data.message || 'Failed to fetch maintenance requests');
        }
      } catch (err) {
        console.error('Error fetching maintenance requests:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMaintenanceRequests();
  }, [token, activeTab]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const response = await fetch(`/api/user/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (data.success) {
        setUserData({
          ...userData,
          ...formData
        });
        setSuccessMessage(<Translation textKey="profileUpdated" />);
        setEditMode(false);
        
        // Update local storage user data
        const storedUser = JSON.parse(localStorage.getItem('user'));
        localStorage.setItem('user', JSON.stringify({
          ...storedUser,
          name: formData.name
        }));
      } else {
        throw new Error(data.message || <Translation textKey="failedToUpdate" />);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Clear messages after 3 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);
  
  // Handle property click
  const handlePropertyClick = (propertyId) => {
    navigate(`/properties/${propertyId}`);
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'badge-warning';
      case 'in progress':
        return 'badge-info';
      case 'completed':
        return 'badge-success';
      case 'rejected':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };
  
  // Get status icon
  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return faClock;
      case 'in progress':
        return faClock;
      case 'completed':
        return faCheckCircle;
      case 'rejected':
        return faTimesCircle;
      default:
        return faClock;
    }
  };
  
  // Render account info section
  const renderAccountInfo = () => {
    return (
      <Card className="profile-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0"><Translation textKey="accountInfo" /></h5>
          <Button 
            variant="outline-primary" 
            size="sm" 
            onClick={() => setEditMode(!editMode)}
          >
            <FontAwesomeIcon icon={faEdit} /> {editMode ? <Translation textKey="cancel" /> : <Translation textKey="edit" />}
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : editMode ? (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="name" className="form-label"><Translation textKey="fullName" /></label>
                <input
                  type="text"
                  className="form-control"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="email" className="form-label"><Translation textKey="emailAddress" /></label>
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-3">
                <label htmlFor="phone" className="form-label"><Translation textKey="phoneNumber" /></label>
                <input
                  type="tel"
                  className="form-control"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="address" className="form-label"><Translation textKey="address" /></label>
                <textarea
                  className="form-control"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows="3"
                ></textarea>
              </div>
              <div className="d-grid">
                <Button 
                  type="submit" 
                  variant="primary" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                      <span className="ms-2"><Translation textKey="saving" /></span>
                    </>
                  ) : <Translation textKey="saveChanges" />}
                </Button>
              </div>
            </form>
          ) : userData ? (
            <div className="user-info-container">
              <div className="user-info-item">
                <span className="info-label"><Translation textKey="fullName" />:</span>
                <span className="info-value">{userData.name}</span>
              </div>
              <div className="user-info-item">
                <span className="info-label"><Translation textKey="emailAddress" />:</span>
                <span className="info-value">{userData.email}</span>
              </div>
              <div className="user-info-item">
                <span className="info-label"><Translation textKey="phoneNumber" />:</span>
                <span className="info-value">{userData.phone || <Translation textKey="notProvided" />}</span>
              </div>
              <div className="user-info-item">
                <span className="info-label"><Translation textKey="address" />:</span>
                <span className="info-value">{userData.address || <Translation textKey="notProvided" />}</span>
              </div>
              <div className="user-info-item">
                <span className="info-label"><Translation textKey="memberSince" />:</span>
                <span className="info-value">
                  {new Date(userData.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-center"><Translation textKey="noUserData" /></p>
          )}
        </Card.Body>
      </Card>
    );
  };
  
  // Render viewed properties section
  const renderViewedProperties = () => {
    return (
      <Card className="profile-card">
        <Card.Header>
          <h5 className="mb-0"><Translation textKey="recentlyViewedProperties" /></h5>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : viewedProperties.length > 0 ? (
            <div className="property-list">
              {viewedProperties.map(property => (
                <div 
                  key={property.id} 
                  className="property-card" 
                  onClick={() => handlePropertyClick(property.id)}
                >
                  <div className="property-image">
                    <img 
                      src={property.image_url || 'https://via.placeholder.com/300x200?text=No+Image'} 
                      alt={property.title} 
                    />
                    <span className="property-price">${property.price.toLocaleString()}</span>
                  </div>
                  <div className="property-details">
                    <h5 className="property-title">{property.title}</h5>
                    <p className="property-address">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> {property.location}
                    </p>
                    <div className="property-features">
                      <span><FontAwesomeIcon icon={faBed} /> {property.bedrooms} <Translation textKey="beds" /></span>
                      <span><FontAwesomeIcon icon={faBath} /> {property.bathrooms} <Translation textKey="baths" /></span>
                      <span><FontAwesomeIcon icon={faRulerCombined} /> {property.area} <Translation textKey="sqft" /></span>
                    </div>
                    <div className="property-viewed-date">
                      <FontAwesomeIcon icon={faCalendarAlt} /> <Translation textKey="viewedOn" /> {new Date(property.viewed_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                    </div>
                    <Button variant="outline-primary" className="view-property-btn">
                      <FontAwesomeIcon icon={faEye} /> <Translation textKey="viewAgain" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p><Translation textKey="noPropertiesViewed" /></p>
              <Button variant="primary" onClick={() => navigate('/properties')}>
                <Translation textKey="browseProperties" />
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };
  
  // Render maintenance requests section
  const renderMaintenanceRequests = () => {
    return (
      <Card className="profile-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0"><Translation textKey="maintenanceRequests" /></h5>
          <Button 
            variant="primary" 
            size="sm" 
            onClick={() => navigate('/maintenance-request')}
          >
            <Translation textKey="newRequest" />
          </Button>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : maintenanceRequests.length > 0 ? (
            <div className="maintenance-list">
              {maintenanceRequests.map(request => (
                <div key={request.id} className="maintenance-card">
                  <div className="maintenance-header">
                    <h5>{request.title}</h5>
                    <span className={`status-badge ${getStatusBadgeClass(request.status)}`}>
                      <FontAwesomeIcon icon={getStatusIcon(request.status)} /> <Translation textKey={request.status.toLowerCase().replace(' ', '')} />
                    </span>
                  </div>
                  <div className="maintenance-body">
                    <p className="maintenance-description">{request.description}</p>
                    <div className="maintenance-meta">
                      <span className="maintenance-date">
                        <FontAwesomeIcon icon={faCalendarAlt} /> <Translation textKey="submittedOn" /> {new Date(request.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                      </span>
                      {request.property && (
                        <span className="maintenance-property">
                          <FontAwesomeIcon icon={faBuilding} /> {request.property.title}
                        </span>
                      )}
                    </div>
                  </div>
                  {request.admin_response && (
                    <div className="maintenance-response">
                      <h6><Translation textKey="adminResponse" /></h6>
                      <p>{request.admin_response}</p>
                      <span className="response-date">
                        {new Date(request.response_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p><Translation textKey="noMaintenanceRequests" /></p>
              <Button variant="primary" onClick={() => navigate('/maintenance-request')}>
                <Translation textKey="submitRequest" />
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };
  
  // Render settings section
  const renderSettings = () => {
    return (
      <Card className="profile-card">
        <Card.Header>
          <h5 className="mb-0"><Translation textKey="accountSettings" /></h5>
        </Card.Header>
        <Card.Body>
          <div className="settings-section">
            <h6><Translation textKey="password" /></h6>
            <Button variant="outline-primary"><Translation textKey="changePassword" /></Button>
          </div>
          <div className="settings-section">
            <h6><Translation textKey="notifications" /></h6>
            <div className="form-check form-switch">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="emailNotifications" 
                defaultChecked 
              />
              <label className="form-check-label" htmlFor="emailNotifications">
                <Translation textKey="emailNotifications" />
              </label>
            </div>
            <div className="form-check form-switch">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="smsNotifications" 
              />
              <label className="form-check-label" htmlFor="smsNotifications">
                <Translation textKey="smsNotifications" />
              </label>
            </div>
          </div>
          <div className="settings-section">
            <h6><Translation textKey="privacy" /></h6>
            <div className="form-check form-switch">
              <input 
                className="form-check-input" 
                type="checkbox" 
                id="saveViewHistory" 
                defaultChecked 
              />
              <label className="form-check-label" htmlFor="saveViewHistory">
                <Translation textKey="savePropertyViewHistory" />
              </label>
            </div>
          </div>
          <div className="settings-section border-0">
            <h6><Translation textKey="account" /></h6>
            <Button variant="outline-danger"><Translation textKey="deactivateAccount" /></Button>
          </div>
        </Card.Body>
      </Card>
    );
  };
  
  if (!user) {
    return null; // Will redirect in useEffect
  }
  
  return (
    <Container className="py-5 user-profile-container">
      <Row>
        <Col md={3}>
          <div className="profile-sidebar">
            <div className="user-profile-header">
              <div className="profile-avatar">
                <FontAwesomeIcon icon={faUser} size="2x" />
              </div>
              <h4>{user.name || user.fullName}</h4>
              <p>{user.email}</p>
            </div>
            <Nav className="flex-column profile-nav">
              <Nav.Link 
                className={activeTab === 'account' ? 'active' : ''} 
                onClick={() => {
                  setActiveTab('account');
                  navigate('/profile?tab=account');
                }}
              >
                <FontAwesomeIcon icon={faUser} /> <Translation textKey="accountInfoShort" />
              </Nav.Link>
              <Nav.Link 
                className={activeTab === 'properties' ? 'active' : ''} 
                onClick={() => {
                  setActiveTab('properties');
                  navigate('/profile?tab=properties');
                }}
              >
                <FontAwesomeIcon icon={faBuilding} /> <Translation textKey="viewedProperties" />
              </Nav.Link>
              <Nav.Link 
                className={activeTab === 'requests' ? 'active' : ''} 
                onClick={() => {
                  setActiveTab('requests');
                  navigate('/profile?tab=requests');
                }}
              >
                <FontAwesomeIcon icon={faClipboardList} /> <Translation textKey="maintenanceRequests" />
              </Nav.Link>
              <Nav.Link 
                className={activeTab === 'settings' ? 'active' : ''} 
                onClick={() => {
                  setActiveTab('settings');
                  navigate('/profile?tab=settings');
                }}
              >
                <FontAwesomeIcon icon={faCog} /> <Translation textKey="settings" />
              </Nav.Link>
            </Nav>
          </div>
        </Col>
        <Col md={9}>
          {successMessage && (
            <Alert variant="success" className="mb-4">
              {successMessage}
            </Alert>
          )}
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          <div className="profile-content">
            {activeTab === 'account' && renderAccountInfo()}
            {activeTab === 'properties' && renderViewedProperties()}
            {activeTab === 'requests' && renderMaintenanceRequests()}
            {activeTab === 'settings' && renderSettings()}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default UserProfilePage;
